import { useRef, useState, useMemo, type ChangeEvent } from 'react'
import {
  Box,
  Button,
  Card,
  CardHeader,
  CardBody,
  Heading,
  HStack,
  VStack,
  Text,
  Input,
  InputGroup,
  InputRightElement,
  Select,
  Tabs,
  TabList,
  Tab,
  Badge,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from '@chakra-ui/react'
import { Search } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { AdminLayout } from '@/components/layout/AdminLayout'
import { DataTable, type DataTableRef, type DataTableOptions } from '@/components/tables'
import { ActionButtons } from '@/components/common/ActionButtons'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { useAccessCheck } from '@/hooks/useAccess'
import { exportToCSV } from '@/lib/utils/csvUtils'
import { apiClient } from '@/lib/api/client'
import { useThemeColors } from '@/hooks/useThemeColors'

type TabValue = 'all' | 'trash'

export default function EmailTemplatePage() {
  const colors = useThemeColors()
  const navigate = useNavigate()
  const tableRef = useRef<DataTableRef>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const { showError, showSuccess, showWarning } = useToastNotification()
  const { isOpen, onOpen, onClose } = useDisclosure()

  const [activeTab, setActiveTab] = useState<TabValue>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [totalRows, setTotalRows] = useState(0)
  const [totalTrash, setTotalTrash] = useState(0)
  const [bulkAction, setBulkAction] = useState('')
  const [isExporting, setIsExporting] = useState(false)
  const [isBulkProcessing, setIsBulkProcessing] = useState(false)
  const [modalMessage, setModalMessage] = useState('')

  const access = useAccessCheck()

  const statusFilterOptions = useMemo(
    () => [
      { value: 'true', label: 'Active' },
      { value: 'false', label: 'Inactive' },
    ],
    []
  )

  const tableOptions: DataTableOptions = useMemo(
    () => ({
      dataFields: {
        name: {
          name: 'Name',
          withSort: true,
          maxWidth: '200px',
        },
        type: {
          name: 'Type',
          withSort: true,
          maxWidth: '150px',
        },
        subject: {
          name: 'Subject',
          withSort: false,
          maxWidth: '300px',
        },
        receiver: {
          name: 'Receiver',
          withSort: false,
          maxWidth: '200px',
        },
        is_active: {
          name: 'Status',
          withSort: true,
          withFilter: true,
          filterOptions: statusFilterOptions,
          badge: {
            true: 'green',
            false: 'yellow',
          },
          badgeLabels: {
            true: 'Active',
            false: 'Inactive',
          },
        },
        updated_at: {
          name: 'Updated At',
          withSort: true,
          maxWidth: '150px',
        },
      },
      dataSource: activeTab === 'trash' ? '/system-settings/archived/email-template' : '/system-settings/email-template',
      primaryKey: 'id',
      softDelete: true,
      displayInModal: false,
      noRecordText: 'No email templates found',
    }),
    [activeTab, statusFilterOptions]
  )

  const permissions = useMemo(
    () => ({
      can_edit: access.can_edit,
      can_delete: access.can_delete,
      can_create: access.can_create,
      can_view: access.can_view,
      can_export: access.can_export,
      can_import: access.can_import,
    }),
    [access]
  )

  const tableParams = useMemo(() => ({ search: searchQuery }), [searchQuery])

  const showSubSubCallback = useMemo(
    () => (all: number, trashed: number) => {
      setTotalRows(all)
      setTotalTrash(trashed)
    },
    []
  )

  const handleTabChange = (index: number) => {
    setActiveTab(index === 0 ? 'all' : 'trash')
    setSearchQuery('')
    if (searchRef.current) {
      searchRef.current.value = ''
    }
    tableRef.current?.clearPage()
  }

  const handleSearch = () => {
    const query = searchRef.current?.value || ''
    setSearchQuery(query)
    tableRef.current?.applyFilter('search', query)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  const handleBulkActionClick = () => {
    if (!bulkAction) {
      showWarning('Please select an action first')
      return
    }
    const selectedIds = tableRef.current?.getSelectedRows() || []
    if (selectedIds.length === 0) {
      showWarning('Please select at least one item')
      return
    }

    if (bulkAction === 'delete' && activeTab === 'trash') {
      setModalMessage('You are about to permanently delete these items from your site. This action cannot be undone.')
    } else {
      setModalMessage('Are you sure to apply this change?')
    }
    onOpen()
  }

  const handleConfirmBulkAction = async () => {
    const selectedIds = tableRef.current?.getSelectedRows() || []
    if (selectedIds.length === 0) return

    setIsBulkProcessing(true)
    try {
      let url = ''
      let payload: any = { ids: selectedIds }
      let successMessage = 'Action completed successfully'

      if (bulkAction === 'restore') {
        url = '/system-settings/email-template/bulk/restore'
        successMessage = 'Email templates restored successfully'
      } else if (bulkAction === 'delete') {
        url =
          activeTab === 'trash'
            ? '/system-settings/email-template/bulk/force-delete'
            : '/system-settings/email-template/bulk/delete'
        successMessage =
          activeTab === 'trash' ? 'Email templates permanently deleted successfully' : 'Email templates moved to trash successfully'
      }

      await apiClient.post(url, payload)
      showSuccess('Success', successMessage)
      tableRef.current?.reload()
      setBulkAction('')
      onClose()
    } catch (error: any) {
      showError(error)
    } finally {
      setIsBulkProcessing(false)
    }
  }

  const handleExportData = async () => {
    setIsExporting(true)
    try {
      await exportToCSV({
        endpoint: '/system-settings/email-template',
        selectedIds: [],
        activeTab,
        searchQuery: '',
        csvHeaders: ['ID', 'Name', 'Type', 'Subject', 'Receiver', 'CC', 'Status', 'Created At', 'Updated At', 'Deleted At'],
        dataMapper: (template: any) => [
          template.id || '',
          template.name || '',
          template.type || '',
          template.subject || '',
          template.receiver || '',
          template.cc || '',
          template.is_active ? 'Active' : 'Inactive',
          template.created_at || '',
          template.updated_at || '',
          template.deleted_at || '',
        ],
        filename: `email-templates_export_all${activeTab === 'trash' ? '_trash' : ''}_${new Date().toISOString().split('T')[0]}.csv`,
        showError,
        showSuccess,
        showWarning,
      })
    } catch (error: any) {
      showError(error)
    } finally {
      setIsExporting(false)
    }
  }

  const handleConfirm = () => {
    handleConfirmBulkAction()
  }

  const customRenderers = useMemo(
    () => ({
      is_active: (value: boolean) => {
        return (
          <Badge colorScheme={value ? 'green' : 'yellow'} variant="solid">
            {value ? 'Active' : 'Inactive'}
          </Badge>
        )
      },
      subject: (value: string) => {
        return <Text noOfLines={2}>{value || '-'}</Text>
      },
      receiver: (value: string) => {
        return <Text fontSize="sm">{value || '-'}</Text>
      },
    }),
    []
  )

  return (
    <AdminLayout>
      <Box p={4} h="100%" overflow="hidden" display="flex" flexDirection="column">
        <VStack spacing={2} align="stretch" flex="1" overflow="hidden" minH={0}>
          <Card flexShrink={0} display="flex" flexDirection="column" flex="1" minH={0} overflow="hidden">
            <CardHeader flexShrink={0} py={3}>
              <Heading size="md" mb={1}>
                Email Templates
              </Heading>
              <Text fontSize="sm" color={colors.text}>
                Manage email notification templates used throughout the system
              </Text>
            </CardHeader>

            <CardHeader py={0} px={0} flexShrink={0}>
              <Box pt={2} pb={0}>
                <HStack justify="space-between" align="flex-end" spacing={4} px={4} width="100%">
                  <Tabs index={activeTab === 'all' ? 0 : 1} onChange={handleTabChange}>
                    <TabList borderBottom="none">
                      <Tab>
                        All{' '}
                        <Badge ml={2} colorScheme="blue">
                          {totalRows}
                        </Badge>
                      </Tab>
                      {totalTrash > 0 && (
                        <Tab>
                          Trash{' '}
                          <Badge ml={2} colorScheme="red">
                            {totalTrash}
                          </Badge>
                        </Tab>
                      )}
                    </TabList>
                  </Tabs>
                  <Box pb={2}>
                    <ActionButtons
                      pathname="/system-settings/email-template"
                      onExport={handleExportData}
                      createLabel="Create New Template"
                      isExporting={isExporting}
                    />
                  </Box>
                </HStack>
                <Box borderBottom="2px" borderColor={colors.border} mx={4} />
              </Box>
            </CardHeader>

            <CardHeader py={1} pt={3} pb={2} flexShrink={0}>
              <HStack spacing={4} flexWrap="wrap">
                {access.can_delete && (
                  <HStack spacing={2} minW="250px">
                    <Select
                      placeholder="Bulk actions"
                      size="sm"
                      value={bulkAction}
                      onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                        e.stopPropagation()
                        setBulkAction(e.target.value)
                      }}
                    >
                      {activeTab === 'trash' && <option value="restore">Restore</option>}
                      {activeTab === 'trash' && <option value="delete">Delete Permanently</option>}
                      {activeTab === 'all' && <option value="delete">Delete</option>}
                    </Select>
                    <Button size="sm" colorScheme="blue" onClick={handleBulkActionClick}>
                      Apply
                    </Button>
                  </HStack>
                )}

                <Box flex="0 1 auto" minW="250px" maxW="400px">
                  <InputGroup size="sm">
                    <Input
                      ref={searchRef}
                      placeholder="Search by name, type, or subject..."
                      onKeyDown={handleKeyDown}
                      defaultValue={searchQuery}
                      pr="2.5rem"
                    />
                    <InputRightElement width="2.5rem">
                      <IconButton
                        aria-label="Search"
                        icon={<Search size={16} />}
                        size="sm"
                        h="1.75rem"
                        w="1.75rem"
                        onClick={handleSearch}
                        variant="ghost"
                      />
                    </InputRightElement>
                  </InputGroup>
                </Box>
              </HStack>
            </CardHeader>

            <CardBody
              p={0}
              pt={1}
              pb={4}
              mx={5}
              flex="1"
              overflow="hidden"
              display="flex"
              flexDirection="column"
              minH={0}
              position="relative"
            >
              <Box h="100%" display="flex" flexDirection="column" minH={0}>
                <DataTable
                  ref={tableRef}
                  options={tableOptions}
                  params={tableParams}
                  access={permissions}
                  setSubSub={showSubSubCallback}
                  onOpenModal={(id) => navigate(`/system-settings/email-template/${id}`)}
                  filterOptions={{
                    is_active: statusFilterOptions,
                  }}
                  customRenderers={customRenderers}
                />
              </Box>
            </CardBody>
          </Card>
        </VStack>
      </Box>

      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirmation</ModalHeader>
          <ModalBody>
            <Text>{modalMessage}</Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose} isDisabled={isBulkProcessing}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleConfirm} isLoading={isBulkProcessing} loadingText="Processing...">
              Confirm
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </AdminLayout>
  )
}
